import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './lib/supabase';
import { theme } from './lib/theme';
import { getUserRole, getPermissions, canAccess, MODULES } from './lib/permissions';
import {
  mockLeads, mockMaintenanceRequests, mockVendorQuotes, mockPropertyVendors,
  mockTransactions, mockBrokerageData, mockPMData, mockReconciliation,
  mockRecentTransactions, mockDashboardStats,
} from './lib/mockData';

// Layout
import Sidebar from './components/layout/Sidebar';
import TopBar from './components/layout/TopBar';
import MobileNav, { FloatingActionButton } from './components/layout/MobileNav';

// Pages
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import ClientPortal from './pages/ClientPortal';
import DashboardHome from './components/dashboard/DashboardHome';
import LeadsView from './components/leads/LeadsView';
import MaintenanceView from './components/maintenance/MaintenanceView';
import TransactionsView from './components/transactions/TransactionsView';
import FinancialsView from './components/financials/FinancialsView';
import AddLeadForm from './components/leads/AddLeadForm';
// SettingsView merged into SettingsPage (unified settings with horizontal tabs)
import AgentDashboard from './components/dashboard/AgentDashboard';
// CommissionsView merged into FinancialsView — role-based rendering
import AgentActivityView from './components/activity/AgentActivityView';
import ListingHub from './components/listings/ListingHub';
// StudioView merged into ListingHub — no longer a standalone route
// IntegrationsView merged into SettingsPage — now a tab inside unified Settings
import CalendarView from './components/calendar/CalendarView';
// ShowingsPage removed — showings folded into CRM lead detail
import MyDayView from './components/agent/MyDayView';
import ReportsPage from './components/reports/ReportsPage';
import RecruitingCRM from './components/recruiting/RecruitingCRM';
import PMView from './components/propertyManagement/PMView';
import SettingsPage from './components/settings/SettingsPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import CommandPalette, { useCommandPalette } from './components/common/CommandPalette';
import { useDarkMode } from './hooks/useDarkMode';
import OnboardingFlow from './components/onboarding/OnboardingFlow';
import { createConfusionTracker } from './lib/confusionTracker';
import GuidedTour from './components/common/GuidedTour';
import TrialBanner from './components/common/TrialBanner';
import BrainChat from './components/ai-ask/BrainChat';
import { TOUR_MAP } from './lib/tourSteps';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';

export default function App() {
  const [user, setUser] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [demoRole, setDemoRole] = useState('broker'); // Role selector in demo mode
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [crmSubTab, setCrmSubTab] = useState(null); // e.g. 'calendar' from TopBar shortcut
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [brainOpen, setBrainOpen] = useState(false);
  const [brainMessage, setBrainMessage] = useState(null);
  const [showLanding, setShowLanding] = useState(true); // Show landing page first
  const [legalPage, setLegalPage] = useState(null); // 'terms' | 'privacy' | null
  const [org, setOrg] = useState(null); // Organization data for trial banner etc.
  const cmdPalette = useCommandPalette();
  const [_isDark] = useDarkMode(); // triggers re-render on theme change

  // Callback for Welcome Hero → BrainChat
  const handleBrainOpen = (message) => {
    setBrainMessage(message || null);
    setBrainOpen(true);
  };
  const handleBrainHandled = () => {
    setBrainOpen(false);
    setBrainMessage(null);
  };

  // Data state (starts with mock, replaced by Supabase when connected)
  const [leads, setLeads] = useState(mockLeads);
  const [maintenanceRequests, setMaintenanceRequests] = useState(mockMaintenanceRequests);
  const [transactions] = useState(mockTransactions);

  // Determine user role
  const userRole = demoMode ? demoRole : getUserRole(user);
  const permissions = getPermissions(userRole);

  // Track login count for guided tour (only auto-show for first 3 logins)
  const incrementLoginCount = useCallback(() => {
    try {
      const count = parseInt(localStorage.getItem('sabalos_login_count') || '0');
      localStorage.setItem('sabalos_login_count', String(count + 1));
    } catch { /* localStorage may be unavailable */ }
  }, []);

  // Check auth on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'SIGNED_IN' && session?.user) incrementLoginCount();
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [incrementLoginCount]);

  // Fetch org data when user logs in (needed by TrialBanner, RecruitingCRM, etc.)
  useEffect(() => {
    if (!user || demoMode) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('organizations')
          .select('*')
          .single();
        if (data && !cancelled) setOrg(data);
      } catch { /* org table may not exist yet in early setups */ }
    })();
    return () => { cancelled = true; };
  }, [user, demoMode]);

  // Reset to dashboard when role changes (prevents landing on inaccessible tab)
  useEffect(() => {
    if (!canAccess(userRole, activeTab)) {
      setActiveTab('dashboard');
    }
  }, [userRole, activeTab]);

  // First-run onboarding: show step-by-step wizard for new users.
  // Checks onboarding_progress in the DB (not just localStorage).
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  useEffect(() => {
    if (!user || demoMode) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const r = await fetch('/api/settings?section=onboarding_status', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        const progress = d.onboarding_progress;
        if (!progress || (!progress.completed && !progress.dismissed)) {
          setNeedsOnboarding(true);
        }
      } catch { /* noop */ }
    })();
  }, [user, demoMode]);

  // Confusion tracker — active for first 10 logins
  const [showHelpNudge, setShowHelpNudge] = useState(false);
  const confusionTrackerRef = useRef(null);
  useEffect(() => {
    if (!user || demoMode || needsOnboarding) return;
    const getLoginCount = () => {
      try { return parseInt(localStorage.getItem('sabalos_login_count') || '0'); } catch { return 99; }
    };
    const tracker = createConfusionTracker({
      onConfused: () => setShowHelpNudge(true),
      getLoginCount,
    });
    confusionTrackerRef.current = tracker;
    // Track clicks globally
    const handleClick = (e) => tracker.trackClick(e);
    document.addEventListener('click', handleClick);
    return () => { tracker.destroy(); document.removeEventListener('click', handleClick); };
  }, [user, demoMode, needsOnboarding]);

  // Wrap setActiveTab to also track navigation for confusion detection
  const handleSetActiveTab = useCallback((tab) => {
    setActiveTab(tab);
    confusionTrackerRef.current?.trackNavigation(tab);
  }, []);

  // Responsive listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auth handlers
  const handleLogin = async (email, password, metadata, demo) => {
    if (demo) {
      setDemoMode(true);
      setLoading(false);
      incrementLoginCount();
      return;
    }
    if (metadata) {
      const { error } = await supabase.auth.signUp({ email, password, options: { data: metadata } });
      if (error) throw error;
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    }
  };

  const handleSignOut = async () => {
    if (demoMode) {
      setDemoMode(false);
      setUser(null);
      return;
    }
    await supabase.auth.signOut();
  };

  // CRUD handlers
  const handleAddLead = async (leadData) => {
    const newLead = { id: crypto.randomUUID(), ...leadData, time: 'Just now' };
    setLeads(prev => [newLead, ...prev]);
    showToast('Lead saved successfully!');
  };

  const handleUpdateLead = (leadId, updates) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, ...updates } : l));
    showToast(`Lead moved to ${updates.status}`);
  };

  const handleUpdateMaintenance = (request, action) => {
    if (action === 'add') {
      setMaintenanceRequests(prev => [request, ...prev]);
    } else {
      setMaintenanceRequests(prev => prev.map(r => r.id === request.id ? { ...r, ...request } : r));
    }
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: theme.colors.bg, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentLight})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, color: 'white', fontSize: 24, margin: '0 auto 12px',
          }}>T</div>
          <div style={{ color: theme.colors.textLight, fontSize: 14 }}>Loading Trovva...</div>
        </div>
      </div>
    );
  }

  // Legal pages (accessible from anywhere, no auth required)
  if (legalPage === 'terms') {
    return <TermsOfService onBack={() => setLegalPage(null)} />;
  }
  if (legalPage === 'privacy') {
    return <PrivacyPolicy onBack={() => setLegalPage(null)} />;
  }

  // Client portal (public, no auth — uses magic link token)
  if (window.location.pathname === '/portal') {
    return <ClientPortal />;
  }

  // Landing page (first screen for unauthenticated users)
  if (!user && !demoMode && showLanding) {
    return (
      <LandingPage
        onGetStarted={() => setShowLanding(false)}
        onLegal={setLegalPage}
      />
    );
  }

  // Login screen
  if (!user && !demoMode) {
    return <LoginPage onLogin={handleLogin} onLegal={setLegalPage} />;
  }

  // Main app
  const sidebarWidth = isMobile ? 0 : sidebarExpanded ? 220 : 60;

  const renderContent = () => {
    // Security: block access to modules the role doesn't have permission for
    if (activeTab !== 'dashboard' && !canAccess(userRole, activeTab)) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h3 style={{ color: theme.colors.text, marginBottom: 8 }}>Access Restricted</h3>
          <p style={{ color: theme.colors.textLight, fontSize: 14 }}>
            You don't have access to this module. Contact your broker for permissions.
          </p>
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        // Show different dashboards based on role
        if (permissions.dashboardType === 'agent') {
          return <AgentDashboard user={user} demoMode={demoMode} setActiveTab={handleSetActiveTab} onBrainOpen={handleBrainOpen} />;
        }
        // broker, admin, pm all get the full dashboard for now
        return <DashboardHome stats={mockDashboardStats} setActiveTab={handleSetActiveTab} demoMode={demoMode} onBrainOpen={handleBrainOpen} user={user} org={org} />;

      case 'leads':
        return <LeadsView leads={leads} onAddLead={handleAddLead} onUpdateLead={handleUpdateLead} userRole={userRole} demoMode={demoMode} showToast={showToast} initialTab={crmSubTab} onTabConsumed={() => setCrmSubTab(null)} />;

      case 'activity':
        return <AgentActivityView showToast={showToast} demoMode={demoMode} userRole={userRole} userName={user?.user_metadata?.full_name || ''} />;

      case 'maintenance':
        return <MaintenanceView requests={maintenanceRequests} vendorQuotes={mockVendorQuotes} propertyVendors={mockPropertyVendors} onUpdateRequest={handleUpdateMaintenance} showToast={showToast} demoMode={demoMode} />;

      case 'transactions':
        return <TransactionsView transactions={transactions} userRole={userRole} />;

      case 'listings':
        return <ListingHub showToast={showToast} demoMode={demoMode} />;

      case 'financials':
        return <FinancialsView brokerageData={mockBrokerageData} pmData={mockPMData} reconciliation={mockReconciliation} recentTransactions={mockRecentTransactions} userRole={userRole} user={user} demoMode={demoMode} showToast={showToast} />;

      // commissions merged into financials — role-based rendering handles agent vs broker view

      // ai_tools merged into listings — StudioView renders inside ListingHub

      // settings + integrations merged into settings_full (SettingsPage with horizontal tabs)

      case 'calendar':
        // Calendar now lives inside CRM — redirect there
        handleSetActiveTab('leads');
        setCrmSubTab('calendar');
        return null;

      case 'my_day':
        return <MyDayView user={user} demoMode={demoMode} />;

      case 'reports':
        return <ReportsPage user={user} demoMode={demoMode} />;

      // showings case removed — folded into CRM lead detail

      case 'recruiting':
        return <RecruitingCRM user={user} org={org} showToast={showToast} />;

      case 'settings_full':
        return <SettingsPage user={user} demoMode={demoMode} org={org} showToast={showToast} userRole={userRole} />;

      case 'pm':
        return <PMView user={user} demoMode={demoMode} showToast={showToast} />;

      default:
        return <DashboardHome stats={mockDashboardStats} setActiveTab={handleSetActiveTab} demoMode={demoMode} user={user} org={org} />;
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: '100vh',
      backgroundColor: theme.colors.bg,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      <TrialBanner org={org} onUpgrade={() => handleSetActiveTab('settings_full')} />
      <div style={{ display: 'flex', flex: 1 }}>
      {!isMobile && (
        <Sidebar
          activeTab={activeTab} setActiveTab={handleSetActiveTab}
          expanded={sidebarExpanded} setExpanded={setSidebarExpanded}
          user={user} userRole={userRole}
        />
      )}
      <div style={{ flex: 1, marginLeft: sidebarWidth, marginBottom: isMobile ? 60 : 0, transition: 'margin-left 0.2s ease' }}>
        <TopBar
          sidebarExpanded={sidebarExpanded} setSidebarExpanded={setSidebarExpanded}
          onSignOut={handleSignOut} user={user} userRole={userRole}
          onNavigate={(page, subTab) => { handleSetActiveTab(page); if (subTab) setCrmSubTab(subTab); }}
        />
        <ErrorBoundary name={MODULES[activeTab]?.label || activeTab} key={activeTab}>
          {renderContent()}
        </ErrorBoundary>
      </div>
      {isMobile && <MobileNav activeTab={activeTab} setActiveTab={handleSetActiveTab} userRole={userRole} moreOpen={mobileMoreOpen} setMoreOpen={setMobileMoreOpen} />}
      </div>

      {/* Guided Tour - auto-starts only for first 3 logins on dashboard */}
      {TOUR_MAP[activeTab] && (
        <GuidedTour
          key={activeTab}
          tourId={activeTab}
          steps={TOUR_MAP[activeTab]}
          autoStart={activeTab === 'dashboard' && (() => {
            try {
              const count = parseInt(localStorage.getItem('sabalos_login_count') || '0');
              return count <= 3 && !window.__tourCompleted?.dashboard;
            } catch { return false; }
          })()}
        />
      )}
      {isMobile && canAccess(userRole, 'leads') && <FloatingActionButton onClick={() => setShowQuickAdd(true)} />}
      {showQuickAdd && <AddLeadForm onClose={() => setShowQuickAdd(false)} onSave={handleAddLead} />}
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16,
          background: theme.colors.success, color: 'white',
          padding: '10px 18px', borderRadius: 10,
          fontWeight: 600, fontSize: 13, zIndex: 300,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>{toast}</div>
      )}

      {needsOnboarding && (
        <OnboardingFlow
          user={user}
          onComplete={() => { setNeedsOnboarding(false); showToast('Welcome to Trovva! You\'re all set.'); }}
          onExit={() => { setNeedsOnboarding(false); showToast('No worries — you can finish setup anytime from Settings.'); }}
        />
      )}

      {/* Help nudge — confusion tracker detected the user is lost */}
      {showHelpNudge && !needsOnboarding && (
        <div style={{
          position: 'fixed', bottom: 90, right: 24, zIndex: 250,
          background: 'white', borderRadius: 16, padding: '16px 20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxWidth: 300,
          border: `2px solid ${theme.colors.accent}`,
          animation: 'helpNudgeFadeIn 0.4s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 24, lineHeight: 1 }}>🧠</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text, marginBottom: 4 }}>
                Need a hand?
              </div>
              <div style={{ fontSize: 13, color: theme.colors.textLight, lineHeight: 1.4, marginBottom: 12 }}>
                It looks like you might be looking for something. I can help you navigate — just ask!
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    setShowHelpNudge(false);
                    confusionTrackerRef.current?.reset();
                    handleBrainOpen('I\'m having trouble finding my way around. Can you help me navigate Trovva?');
                  }}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: 'none',
                    background: theme.colors.accent, color: 'white',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Yes, help me
                </button>
                <button
                  onClick={() => {
                    setShowHelpNudge(false);
                    confusionTrackerRef.current?.reset();
                  }}
                  style={{
                    padding: '6px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
                    background: 'transparent', color: theme.colors.textLight,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  I'm good
                </button>
              </div>
            </div>
            <button
              onClick={() => { setShowHelpNudge(false); confusionTrackerRef.current?.reset(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textLight, fontSize: 16, padding: 0, lineHeight: 1 }}
            >×</button>
          </div>
          <style>{`@keyframes helpNudgeFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
      )}

      {/* Command Palette — ⌘K */}
      <CommandPalette
        open={cmdPalette.open}
        onClose={cmdPalette.close}
        onNavigate={setActiveTab}
        userRole={userRole}
        permissions={permissions}
      />

      {/* Trovva Brain — floating AI chat */}
      <BrainChat user={user} demoMode={demoMode} externalOpen={brainOpen} externalMessage={brainMessage} onExternalHandled={handleBrainHandled} activeTab={activeTab} />

      {/* Demo mode banner with role switcher */}
      {demoMode && (
        <div style={{
          position: 'fixed', bottom: isMobile ? 66 : 12, left: '50%', transform: 'translateX(-50%)',
          background: theme.colors.primaryDark, color: 'white',
          padding: '8px 16px', borderRadius: 20,
          fontSize: 12, fontWeight: 600, zIndex: 300,
          display: 'flex', alignItems: 'center', gap: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          <span>Demo Mode</span>
          <span style={{ opacity: 0.5 }}>|</span>
          <span style={{ opacity: 0.7 }}>View as:</span>
          {['broker', 'agent', 'pm_manager'].map(r => (
            <button
              key={r}
              onClick={() => setDemoRole(r)}
              style={{
                padding: '3px 10px', borderRadius: 12, border: 'none',
                background: demoRole === r ? theme.colors.accent : 'rgba(255,255,255,0.15)',
                color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {r === 'pm_manager' ? 'PM' : r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
